import React from 'react';
import styled from 'styled-components';
import { media, theme } from 'styles';
import { Link, Element as ScrollElement } from 'react-scroll';
import bannerImage from 'images/banners/how-it-works-banner.jpg';
import process01 from 'images/process-icons/MT_Process-01.png';
import process02 from 'images/process-icons/MT_Process-02.png';
import process03 from 'images/process-icons/MT_Process-03.png';
import process04 from 'images/process-icons/MT_Process-04.png';
import process05 from 'images/process-icons/MT_Process-05.png';
import process06 from 'images/process-icons/MT_Process-06.png';
import process07 from 'images/process-icons/MT_Process-07.png';
import process08 from 'images/process-icons/MT_Process-08.png';
import detailA from 'images/process-details/process-detail-a.png';
import detailC from 'images/process-details/process-detail-c.svg';
import detail01B from 'images/process-details/process-detail-1b.png';
import detail02A from 'images/process-details/process-detail-2a.png';
import detail02B from 'images/process-details/process-detail-2b.png';
import detail03A from 'images/process-details/process-detail-3a.png';
import detail03B from 'images/process-details/process-detail-3b.png';
import detail04A from 'images/process-details/process-detail-4a.png';
import detail04B from 'images/process-details/process-detail-4b.png';
import detailFinal from 'images/process-details/process-detail-final.png';
import {
  Breadcrumbs,
  Button,
  Container,
  SeoTags,
  VideoCallout,
} from 'components/shared';
import FacebookTags from '../components/shared/FacebookTags/FacebookTags';

const { sp, colors } = theme;

const ProcessDetail = styled.div`
  padding: 0 0 100px;

  ${media.tablet`
    padding: 0 0 160px;
  `};
`;

const ProcessDetailHeader = styled.div`
  margin-bottom: 30px;

  ${media.tablet`
    ${props =>
      props.reverse &&
      `
      text-align: right;
    `}
  `};

  h2 {
    margin: 0 0 10px;
    font-size: 32px;
    line-height: 40px;
    font-weight: 700;

    ${media.tablet`
      font-size: 36px;
      line-height: 43px;
    `};
  }

  p {
    margin: 0;
    color: ${colors.royal};
  }
`;

const ProcessDetailWrapper = styled.div`
  display: flex;
  flex-direction: column;

  ${media.tablet`
    flex-direction: row;
  `};
`;

const ProcessDetailPrimary = styled.div`
  order: 1;

  ${media.tablet`
    order: inherit;
    width: 60%;
  `};

  img {
    display: block;
    position: relative;
    max-width: 100%;
  }
`;

const ProcessDetailSecondary = styled.div`
  display: flex;
  align: items: center;
  justify-content: center;
  order: 2;
  background-image: url('${props => props.src}');
  background-size: contain;
  background-position: top center;
  background-repeat: no-repeat;

  img {
    position: relative;
    align-self: center;
    top: -40px;
    max-width: 100%;
    z-index: 1;

    ${media.tablet`
      top: auto;
      left: -60px;
    `};
  }

  ${media.tablet`
    width: 40%;
    order: inherit;

    ${props =>
      props.reverse &&
      `
      img {
        right: -70px;
        left: auto;
      }
    `}
  `};
`;

const ProcessDetailFinal = styled.div`
  position: relative;
  text-align: center;
  background-image: url('${detailFinal}');
  background-size: contain;
  background-position: center center;
  background-repeat: no-repeat;

  h2 {
    margin: 0 0 160px;
    font-size: 50px;
    line-height: 60px;
    font-weight: 700;
    padding: 20px 0;

    ${media.tablet`
      font-size: 64px;
      line-height: 77px;
    `};
  }
`;

const Wrapper = styled.div`
  background-color: ${colors.white};
`;

const Grid = styled.div`
  ${media.phablet`
    display: flex;
    flex-wrap: wrap;
  `};
`;

const GridItem = styled.div`
  margin-bottom: 20px;
  background-color: ${colors.blueIce};
  padding: 60px 30px;
  margin-bottom: 40px;
  border: 1px solid rgba(43, 88, 153, 0.2);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border-radius: 6px;
  text-align: center;
  min-height: 340px;

  &:nth-child(2n) {
    ${media.phablet`
      margin-right: 0;
    `};

    ${media.desktop`
      margin-right: 40px;
    `};
  }

  &:nth-child(4n) {
    ${media.desktop`
      margin-right: 0;
    `};
  }

  ${media.phablet`
    margin-right: 40px;
    width: calc((100% / 2) - (40px / 2));
  `};

  ${media.desktop`
    width: calc((100% / 4) - (120px / 4));
  `};
`;

const GridTitle = styled.div`
  font-size: 16px;
  line-height: 20px;
`;

const GridText = styled.div`
  font-size: 14px;
  line-height: 20px;
  color: ${colors.royal};
`;

const GridIcon = styled.div`
  display: inline-block;
  margin: 30px 0;
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background-color: ${colors.white};

  img {
    max-width: 100%;
  }
`;

const Banner = styled.div`
  position: relative;
  background-image: url('${props => props.src}');
  background-size: cover;
  background-position: top right -150px;
  background-repeat: no-repeat;
  padding: 60px 0 100px;

  ${media.tablet`
    padding: 100px 0;
  `};

  h1 {
    color: ${colors.white};
    max-width: 600px;
    font-size: 32px;
    line-height: 40px;
    letter-spacing: -0.8px;
    margin-top: ${sp.l};
    margin-bottom: ${sp.xl};
    font-weight: 700;

    ${media.tablet`
      font-size: 60px;
      line-height: 72px;
      letter-spacing: -1.1px;
    `};
  }

  &::before {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(43, 88, 153, 0.7);
    content: '';
  }

  ${media.tablet`
    background-position: top right;
    min-height: 960px;

    &::before {
      background-color: rgba(43, 88, 153, 0.3);
    }
  `};
`;

const Wedge = styled.div`
  position: absolute;
  width: 110%;
  bottom: 0;
  left: -10%;

  &::after {
    position: absolute;
    left: 0;
    bottom: 0;
    width: 100%;
    height: 1px;
    background-color: ${colors.blueIce};
    content: '';
  }

  svg {
    display: block;
  }
`;

const Intro = styled.div`
  background-color: ${colors.blueIce};
  padding: 60px 0 100px;
  margin-bottom: 100px;

  ${media.tablet`
    padding: 100px 0 160px;
  `};

  h2 {
    margin: 0 0 20px;
    font-size: 36px;
    line-height: 43px;
  }
`;

const VideoPlaceHolder = styled.div`
  background-color: ${colors.blueIce};
  padding: 20px 0 20px;
  margin-bottom: 100px;

  ${media.tablet`
    padding: 30px 0 40px;
  `};

  h2 {
    margin: 0 0 20px;
    font-size: 36px;
    line-height: 43px;
  }
`;

const Process = styled.div`
  padding: 0 0 160px;
`;

const ProcessHeader = styled.div`
  margin: 0 0 60px;
  text-align: center;

  h2 {
    margin: 0 0 10px;
    font-size: 36px;
    line-height: 43px;
  }

  p {
    margin: 0;
  }
`;

function HowItWorks() {
  const breadcrumbs = [
    {
      name: 'Home',
      link: '/',
    },
    {
      name: 'How It Works',
      link: '/how-it-works',
    },
  ];

  return (
    <Wrapper>
      <SeoTags
        title="How MarketTap's Platform Works | MarketTap"
        description="MarketTap is a simple marketing platform that allows you to buy on demand marketing services and track their delivery. Interested? Get in touch today."
      />

      <FacebookTags
        fbUrl={`${process.env.GATSBY_MARKETTAP_SITE_HOST}/how-it-works/`}
        fbTitle="How MarketTap's Platform Works | MarketTap"
        fbDescription="MarketTap is a simple marketing platform that allows you to buy on demand marketing services and track their delivery. Interested? Get in touch today."
        fbImage={bannerImage}
      />

      <Banner src={bannerImage}>
        <Wedge>
          <svg viewBox="0 0 1400 100">
            <polygon points="0 0 1400 100 0 100" fill="#f7faff" />
          </svg>
        </Wedge>

        <Container>
          <Breadcrumbs breadcrumbs={breadcrumbs} left light />

          <h1>
            Our platform is changing the way businesses manage their marketing.
          </h1>
          <Link to="howItWorks" smooth duration={1000}>
            <Button yellow scroll>
              Find out how
            </Button>
          </Link>
        </Container>
      </Banner>
      <ScrollElement name="howItWorks">
        <Intro>
          <Container narrow centerText>
            <h2>How it works</h2>
          </Container>
          <VideoPlaceHolder>
            <Container>
              <VideoCallout
                heading=""
                subheading=""
                buttonText=""
                videoThumbnail=""
                videoID="yd7qih7mq8"
              />
            </Container>
          </VideoPlaceHolder>
          <Container narrow centerText>
            <p>
              With MarketTap, you are able to shop services, chat with us live,
              track the progress of your projects and more… all within one
              platform! It’s as easy and convenient as shopping online.
            </p>
          </Container>
        </Intro>
      </ScrollElement>
      <ProcessDetail>
        <Container>
          <ProcessDetailHeader>
            <h2>1. Shop Services</h2>
            <p>Browse our range of pay-as-you-go marketing services.</p>
          </ProcessDetailHeader>
          <ProcessDetailWrapper>
            <ProcessDetailPrimary>
              <img src={detailA} alt="" />
            </ProcessDetailPrimary>
            <ProcessDetailSecondary src={detailC}>
              <img src={detail01B} alt="" />
            </ProcessDetailSecondary>
          </ProcessDetailWrapper>
        </Container>
      </ProcessDetail>
      <ProcessDetail>
        <Container>
          <ProcessDetailHeader reverse>
            <h2>2. Book Services</h2>
            <p>Add to cart and checkout within our platform.</p>
          </ProcessDetailHeader>
          <ProcessDetailWrapper>
            <ProcessDetailSecondary reverse src={detailC}>
              <img src={detail02B} alt="" />
            </ProcessDetailSecondary>
            <ProcessDetailPrimary>
              <img src={detail02A} alt="" />
            </ProcessDetailPrimary>
          </ProcessDetailWrapper>
        </Container>
      </ProcessDetail>
      <ProcessDetail>
        <Container>
          <ProcessDetailHeader>
            <h2>3. Add Details</h2>
            <p>
              Answer some initial kick off questions and your project begins!
            </p>
          </ProcessDetailHeader>
          <ProcessDetailWrapper>
            <ProcessDetailPrimary>
              <img src={detail03A} alt="" />
            </ProcessDetailPrimary>
            <ProcessDetailSecondary src={detailC}>
              <img src={detail03B} alt="" />
            </ProcessDetailSecondary>
          </ProcessDetailWrapper>
        </Container>
      </ProcessDetail>
      <ProcessDetail>
        <Container>
          <ProcessDetailHeader reverse>
            <h2>4. Review & Approve</h2>
            <p>Finished projects are uploaded for your review and approval</p>
          </ProcessDetailHeader>
          <ProcessDetailWrapper>
            <ProcessDetailSecondary reverse src={detailC}>
              <img src={detail04B} alt="" />
            </ProcessDetailSecondary>
            <ProcessDetailPrimary>
              <img src={detail04A} alt="" />
            </ProcessDetailPrimary>
          </ProcessDetailWrapper>
        </Container>
      </ProcessDetail>
      <ProcessDetailFinal>
        <h2>5.See Results</h2>
      </ProcessDetailFinal>
      <Process>
        <Container>
          <ProcessHeader>
            <h2>Our process for delivering results</h2>
            <p>
              While each individual project is different, here are the typical
              stages of our project delivery:
            </p>
          </ProcessHeader>
          <Grid>
            <GridItem>
              <GridTitle>1. Your Input</GridTitle>
              <GridIcon>
                <img src={process01} alt="" />
              </GridIcon>
              <GridText>
                As soon as you check out, you will be presented with initial
                kick off questions. Once these are answered, your project
                officially starts!
              </GridText>
            </GridItem>

            <GridItem>
              <GridTitle>2. Assign Team</GridTitle>
              <GridIcon>
                <img src={process02} alt="" />
              </GridIcon>
              <GridText>
                Within 24 hours, MarketTap experts are assigned to you. Your
                Success Manager will reach out to schedule an initial call.
              </GridText>
            </GridItem>

            <GridItem>
              <GridTitle>3. Creation</GridTitle>
              <GridIcon>
                <img src={process03} alt="" />
              </GridIcon>
              <GridText>Our experts get to work on your project!</GridText>
            </GridItem>

            <GridItem>
              <GridTitle>4. Internal Review</GridTitle>
              <GridIcon>
                <img src={process04} alt="" />
              </GridIcon>
              <GridText>
                All projects go through several internal quality reviews and
                revisions to ensure they are up to MarketTap standards.
              </GridText>
            </GridItem>

            <GridItem>
              <GridTitle>5. Project Upload</GridTitle>
              <GridIcon>
                <img src={process05} alt="" />
              </GridIcon>
              <GridText>
                Your project is completed! You will be notified through the
                platform as soon as any completed work is uploaded.
              </GridText>
            </GridItem>

            <GridItem>
              <GridTitle>6. Your Review</GridTitle>
              <GridIcon>
                <img src={process06} alt="" />
              </GridIcon>
              <GridText>
                Your Success Manager will reach out to schedule a call to review
                the project together and answer any questions.
              </GridText>
            </GridItem>

            <GridItem>
              <GridTitle>7. Implementation</GridTitle>
              <GridIcon>
                <img src={process07} alt="" />
              </GridIcon>
              <GridText>
                For services like social media advertising and SEO, we implement
                the recommendations we created for you.
              </GridText>
            </GridItem>

            <GridItem>
              <GridTitle>8. Measure & Report</GridTitle>
              <GridIcon>
                <img src={process08} alt="" />
              </GridIcon>
              <GridText>
                For services like social media advertising and SEO, we will
                present relevant and ongoing metrics reports to measure the
                success of your project.
              </GridText>
            </GridItem>
          </Grid>
        </Container>
      </Process>
    </Wrapper>
  );
}

export default HowItWorks;



// WEBPACK FOOTER //
// ./src/pages/how-it-works.js